import { Box, ButtonBase, Paper, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If, Else } from "lib/@components/switch"
import { ShareLinkCommentAnchorHeader } from "./share-link-comment-anchor-header"
import { ShareLinkCommentMessage } from "./share-link-comment-message"
import { ShareLinkCommentComposer } from "./share-link-comment-composer"
import { ShareLinkCommentResolveButton } from "./share-link-comment-resolve-button"
import { ShareLinkCommentFocus } from "./share-link-comment-event-definitions"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useEffect, useRef, useState } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { canViewerReplyToComment } from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"
import { useMarkCommentThreadReadOnView } from "./use-mark-comment-thread-read-on-view"
import { CommentNewMarker } from "./comment-new-marker"
import {
    commentExistsInThreads,
    getCreatedCommentId,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-feed-helpers"
import { getMessageClusters } from "routes/schedule/plugins/share-link-comments/share-link-comment-clusters"

export function ShareLinkCommentThread({
    comment,
    schedule,
    viewer,
    onReply,
    onResolve,
    onEdit,
    onDelete,
    onJump,
    onReveal,
}) {
    const { commentList, unreadThreadSet } = useBoundContext()
    const cardRef = useRef(null)
    // The thread clears its unread state only once its newest message has been seen, so the observer is
    // attached to a sentinel at the bottom of the message list (rendered only when expanded) rather than
    // to the card itself — a deep reply has to actually be scrolled to.
    const attachReadObserver = useMarkCommentThreadReadOnView(comment.threadId ?? comment.id)
    const isUnread = !!unreadThreadSet?.has(comment.threadId ?? comment.id)
    const [selectedCommentId] = useSearchParam("comment", null)
    const [pendingReplyId, setPendingReplyId] = useState(null)
    const [expanded, setExpanded] = useState(!comment.resolved)
    const showResolve = !viewer?.isShareLinkUser
    const isResolved = !!comment.resolved
    // Hover linkage with the in-document highlights: when another surface focuses this comment we
    // raise the card's emphasis by toggling a data attribute (no React re-render), and the card's
    // own hover raises the shared focus event so the matching highlight lights up.
    ShareLinkCommentFocus.useEvent(syncFocusedAttribute, [comment.id])
    const canReply = canViewerReplyToComment(comment, viewer)
    const isSelected = selectedCommentId === comment.id || selectedCommentId === comment.threadId
    const messages = getMessages()
    const clusters = getMessageClusters(messages)

    useEffect(syncExpandedState, [comment.resolved, isSelected])
    useEffect(clearReplyWhenCommentAppears, [commentList, pendingReplyId])

    return (
        <Paper
            ref={cardRef}
            variant="outlined"
            data-cy={`share-link-comment-${comment.id}`}
            data-comment-id={comment.id}
            data-unread={isUnread ? "true" : undefined}
            onMouseEnter={focusComment}
            onMouseLeave={blurComment}
            sx={getCardSx()}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ShareLinkCommentAnchorHeader comment={comment} schedule={schedule} onJump={onJump} />
                    </Box>
                    <If value={isUnread}>
                        <CommentNewMarker />
                    </If>
                </Stack>
                <If value={expanded}>
                    <Stack spacing={0.5}>
                        <Repeat
                            list={clusters}
                            pass="cluster"
                            keyFn={getClusterKey}
                            item={<ShareLinkCommentMessage viewer={viewer} onEdit={onEdit} onDelete={onDelete} />}
                        />
                        {/* Sentinel marking the bottom of the read content: once it has dwelled in view the
                            viewer has scrolled past the newest message, so the thread counts as read. */}
                        <Box ref={attachReadObserver} aria-hidden sx={{ height: 1 }} />
                    </Stack>
                    <If value={!!canReply || showResolve}>
                        <Stack direction="row" spacing={1} alignItems="flex-end" sx={getFooterSx()}>
                            <If value={!!canReply}>
                                <Box sx={getReplySx()}>
                                    <ShareLinkCommentComposer
                                        dataCy={`share-link-comment-reply-${comment.id}`}
                                        label="Reply…"
                                        buttonLabel="Reply"
                                        minRows={1}
                                        publishing={!!pendingReplyId}
                                        onSubmit={submitReply}
                                        onFocus={revealCommentContext}
                                    />
                                </Box>
                            </If>
                            <If value={showResolve}>
                                <ShareLinkCommentResolveButton
                                    resolved={isResolved}
                                    onClick={resolve}
                                    dataCy={`share-link-comment-resolve-${comment.id}`}
                                />
                            </If>
                        </Stack>
                    </If>
                    <Else>
                        <ButtonBase onClick={expand} data-cy="share-link-comment-expand" sx={getSummarySx()}>
                            <Typography variant="caption" color="text.secondary">
                                {getCollapsedSummary()}
                            </Typography>
                        </ButtonBase>
                    </Else>
                </If>
            </Stack>
        </Paper>
    )

    function getMessages() {
        return [comment, ...(comment.replies ?? [])]
    }

    function getClusterKey(cluster, index) {
        return cluster?.message?.id ?? index
    }

    function focusComment() {
        ShareLinkCommentFocus.raise(comment.id)
    }

    function blurComment() {
        ShareLinkCommentFocus.raise(null)
    }

    function syncFocusedAttribute(focusedId) {
        const element = cardRef.current
        if (!element) return
        if (focusedId && focusedId === comment.id) {
            element.setAttribute("data-focused", "true")
        } else {
            element.removeAttribute("data-focused")
        }
    }

    function getCollapsedSummary() {
        return `Resolved · ${messages.length} ${messages.length === 1 ? "message" : "messages"}`
    }

    function getCardSx() {
        const sx = {
            position: "relative",
            overflow: "hidden",
            px: 1.25,
            pt: 1.5,
            pb: 1.25,
            mb: 1.25,
            borderRadius: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            opacity: comment.resolved && !expanded ? 0.7 : 1,
            transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease",
            "&:hover": {
                borderColor: "primary.light",
            },
            '&[data-focused="true"]': {
                borderColor: "primary.light",
                bgcolor: "background.neutral",
            },
        }

        // Accent the left edge of a thread that still has unread content so it stands out in the list.
        if (isUnread) {
            sx.borderLeftWidth = 3
            sx.borderLeftColor = "error.main"
        }

        if (!isSelected) return sx

        sx.borderColor = "primary.light"
        sx.bgcolor = "background.neutral"
        sx.boxShadow = 1
        sx["&::before"] = {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: "primary.main",
        }

        return sx
    }

    function getFooterSx() {
        return {
            pt: 1,
            mt: 0.5,
            borderTop: 1,
            borderColor: "divider",
        }
    }

    function getReplySx() {
        return {
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": {
                bgcolor: "background.paper",
                borderRadius: 4,
            },
        }
    }

    function resolve(event) {
        event.stopPropagation()
        onResolve?.(comment, !isResolved)
    }

    // Focusing the reply field selects the comment (same path as jump), which lifts its document
    // highlight and smooth-scrolls the anchored text into view so the context is visible before
    // the user starts writing.
    function revealCommentContext() {
        // Focusing the reply box reveals the comment's context. On a schedule page that's a soft
        // in-document scroll (onJump). In aggregated views onReveal is overridden (e.g. a no-op) so
        // starting a reply doesn't trigger onJump's navigation. Defaults to onJump for the schedule view.
        ;(onReveal ?? onJump)?.(comment)
    }

    function getSummarySx() {
        return {
            justifyContent: "flex-start",
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
            cursor: "pointer",
            "&:hover": {
                bgcolor: "action.hover",
            },
        }
    }

    function expand() {
        setExpanded(true)
    }

    function syncExpandedState() {
        if (!comment.resolved || isSelected) setExpanded(true)
    }

    async function submitReply(body) {
        const created = await onReply(comment, body)
        const createdId = getCreatedCommentId(created)
        if (createdId) setPendingReplyId(createdId)
    }

    function clearReplyWhenCommentAppears() {
        if (!pendingReplyId) return
        if (!commentExistsInThreads(commentList, pendingReplyId)) return
        setPendingReplyId(null)
    }
}
